// 🔧 user_profile.js
// ✅ Purpose: Admin-only page to add or edit a user profile
// ✅ Features:
// - Works for both new user creation and editing existing users
// - Editable fields: email, first name, last name, phone, role
// - Role dropdown options: [pending, voter, manager, admin] (default is voter)
// - Displays a list of items (cars) owned by the user, if any exist
// - Each item links to its car registration page
// - Returns to AdminUsers page after save or cancel
// - Loads event ID from useEvent() and user ID from URL params
// - Pulls global user profile from /users/{uid} and role from /events/{eventId}/users/{uid}

import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  doc,
  setDoc,
  serverTimestamp,
  collection,
  getDoc,
  getDocs,
} from "firebase/firestore";
import { db, auth  } from "../../firebase";
import { showStatus } from "../../utils/status_helpers";
import FullPageSpinner from "../../components/FullPageSpinner";
import { logger } from '../../utils/logger';
import { useEventContext } from "../../hooks/use_event_context";

const AdminUserProfile = () => {

  const navigate = useNavigate();
  const { userId } = useParams();
  const {
    eventslug,
    eventId,
    eventLoading,
    ready,
  } = useEventContext({ redirectIfNotFound: true });

  const [userProfile, setUserProfile] = useState({
      email: "",
      firstName: "",
      lastName: "",
      phone: "",
    });

  const [role, setRole] = useState("voter");
  const [cars, setCars] = useState([]);
  const [sponsorContacts, setSponsorContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const isNew = userId === "new";
  const uid = isNew ? userProfile.email : userId;
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!status) return;

    const timer = setTimeout(() => {
      setStatus(null);
    }, 20000);

    return () => clearTimeout(timer);
  }, [status]);

  useEffect(() => {
    if (!eventId || !userId) return;

    const fetchData = async () => {
      if (isNew) {
        setLoading(false);
        return;
      }

      try {

        const globalSnap = await getDoc(
          doc(db, "users", userId)
        );

        if (globalSnap.exists()) {
          setUserProfile({
            id: globalSnap.id,
            ...globalSnap.data(),
          });
        }

        logger.log("🧪 Global user data snapshot:", {
          value: globalSnap
        });
        
        // 👇 corrected to proper relative path
        const roleSnap = await getDoc(
          doc(db, "events", eventId, "users", userId)
        );

        if (
          roleSnap.exists() &&
          roleSnap.data()?.role
        ) {
          setRole(roleSnap.data().role);
        }

        // 👇 corrected to proper relative path
        const carsSnap = await getDocs(
          collection(db, "events", eventId, "cars")
        );

        const userCars = carsSnap.docs
          .map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          }))
          .filter((car) => car.ownerId === userId);
                    
        setCars(userCars);

        // ✅ Load sponsors where this user is the Sponsor Point of Contact
        const sponsorsSnap = await getDocs(
          collection(db, "events", eventId, "sponsors")
        );

        const userSponsorContacts = sponsorsSnap.docs
          .map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          }))
          .filter(
            (sponsor) =>
              sponsor.sponsorContactUid === userId ||
              (
                userProfile.email &&
                sponsor.sponsorContactEmail?.toLowerCase() ===
                  userProfile.email.toLowerCase()
              )
          );

        setSponsorContacts(userSponsorContacts);

        logger.log("🌟 Sponsor contact assignments loaded:", {
          userId,
          count: userSponsorContacts.length,
        });

      } catch (err) {
        showStatus(setStatus, {
          type: "error",
          message: "❌ Failed to load user profile.",
        });
        logger.error("❌ Error loading user/profile/cars:", {
          value: err
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [eventId, userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!userProfile.email) {
      if (saving) return;
      setSaving(true);
      showStatus(setStatus, { type: "error", message: "❌ Email is required" });
      return;
    }

    try {
      if (isNew) {
        const pendingRef = doc(collection(db, "users"));
        const pendingUid = pendingRef.id;

        await setDoc(pendingRef, {
          email: userProfile.email.trim().toLowerCase(),
          firstName: userProfile.firstName || "",
          lastName: userProfile.lastName || "",
          phone: userProfile.phone || "",
          role: "pending",
          userType: role === "manager" ? "pendingManager" : "pendingVoter",
          loginMethod: null,
          emailVerified: false,
          invitedAt: serverTimestamp(),
          invitedBy: auth.currentUser?.uid || "",
          inviteEmailSent: false,
          eventInvites: {
            [eventId]: {
              role,
              status: "pending",
              eventId,
              eventslug,
              invitedAt: new Date().toISOString(),
            },
          },
        });

        await setDoc(
          doc(db, "events", eventId, "users", pendingUid),
          {
            role,
            pendingManager: role === "manager",
            pendingVoter: role === "voter",
            status: "pending",
            assignedAt: serverTimestamp(),
          },
          { merge: true }
        );

        showStatus(setStatus, {
          type: "success",
          message: "✅ Pending user invite created.",
        });

        return;
        
      }

      await setDoc(
        doc(db, "users", uid),
        {
          ...userProfile,
          updatedAt: serverTimestamp(),
          createdAt: userProfile.createdAt || serverTimestamp(),
        },
        { merge: true }
      );

      await setDoc(
        doc(db, "events", eventId, "users", uid),
        {
          role,
          assignedAt: serverTimestamp(),
        },
        { merge: true }
      );

      showStatus(setStatus, {
        type: "success",
        message: "✅ User saved successfully",
      });
    } catch (err) {
      logger.error("❌ Error saving user:", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message: "❌ Failed to save user",
      });
    } finally {
      setSaving(false);
    }

  };


  if (!ready || eventLoading || loading) return <FullPageSpinner message="Loading User..." />;

  return (
    <>
      <section id="top-content" className="container-fluid py-5">
        <div className="row mb-4">
          <div className="col text-start">
            <h1 className="display-5">
              {isNew ? "📨 Invite User to Event" : "👤 Edit User"}
            </h1>

            <p className="lead text-muted">
              {isNew
                ? "Invite a voter or manager to join this event"
                : "Manage user details and permissions"}
            </p>
          </div>
        </div>
  
        <div className="white-section p-4 rounded shadow-sm bg-white">

          {status?.message && (
            <div
              className={`alert ${
                status.type === "success"
                  ? "alert-success"
                  : status.type === "warning"
                  ? "alert-warning"
                  : status.type === "error"
                  ? "alert-danger"
                  : "alert-info"
              }`}
              role="alert"
            >
              {status.message}
            </div>
          )}      
          
          <div className="row mb-4">
            <div className="col-md-6 mb-3">
              <label className="form-label">First Name</label>
              <input name="firstName" value={userProfile.firstName} onChange={handleChange} className="form-control" />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">Last Name</label>
              <input name="lastName" value={userProfile.lastName} onChange={handleChange} className="form-control" />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">Email</label>
              <input name="email" value={userProfile.email} disabled={!isNew} onChange={handleChange} className="form-control" type="email" />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">Phone</label>
              <input name="phone" value={userProfile.phone} onChange={handleChange} className="form-control" />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">Role</label>

                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="form-select"
                >
                  {isNew ? (
                    <>
                      <option value="voter">Invite as Voter</option>
                      <option value="manager">Invite as Manager</option>
                    </>
                  ) : (
                    <>
                      {uid === auth.currentUser?.uid && <option value="admin">Admin</option>}
                      <option value="voter">Voter</option>
                      <option value="manager">Manager</option>
                      {role === "pending" && <option value="pending">Pending</option>}
                    </>
                  )}
                </select>

            </div>
          </div>
  
          <div className="d-flex gap-3 mb-5">
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "💾 Save"}
            </button>
            <button className="btn btn-secondary" onClick={() => navigate(`/${eventslug}/users`)}>Cancel</button>
          </div>

          {!isNew && sponsorContacts.length > 0 && (
            <div className="mt-5">
              <h4 className="fw-bold">🌟 Sponsor Contact Assignments</h4>

              <p className="text-muted">
                This user is listed as the Sponsor Point of Contact for the following sponsors.
              </p>

              <ul className="list-group">
                {sponsorContacts.map((sponsor) => (
                  <li
                    key={sponsor.id}
                    className="list-group-item d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2"
                  >
                    <div>
                      <strong>{sponsor.name || "Unnamed Sponsor"}</strong>

                      {sponsor.sponsorAwardTitle && (
                        <div className="small text-muted">
                          🏆 Award: {sponsor.sponsorAwardTitle}
                        </div>
                      )}

                      {sponsor.level && (
                        <div className="small text-muted">
                          🎗 Level: {sponsor.level}
                        </div>
                      )}
                    </div>

                    <Link
                      className="btn btn-sm btn-outline-primary"
                      to={`/${eventslug}/sponsor/${sponsor.sponsorSlug || sponsor.id}`}
                    >
                      View/Edit Sponsor
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!isNew && cars.length > 0 && (
            <div className="mt-5">
              <h4 className="fw-bold">📦 Registered Items</h4>
              <ul className="list-group">
                {cars.map((car) => (
                  <li key={car.id} className="list-group-item d-flex justify-content-between">
                    <div><strong>{car.description}</strong> — {car.regcnt}</div>
                    <Link className="btn btn-sm btn-outline-primary" to={`/${eventslug}/entry_reg/${car.id}`}>View/Edit</Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
  
          {!isNew && cars.length === 0 && (
            <p className="text-muted mt-3">No items registered by this user.</p>
          )}
        </div>
      </section>
    </>
  );

};

export default AdminUserProfile;
